import type { Node, Point } from 'slate';
import { Editor, Element, Path, Text, Transforms } from 'slate';

import {
  createDefaultText,
  getIsSameSuggestionSource,
  Queries,
  suggestionMarkerEndType,
  suggestionMarkerStartType,
} from '../../../internal';
import type {
  AlineText,
  SuggestionMarkerEndElement,
  SuggestionMarkerStartElement,
  SuggestionSource,
} from '../../../types';
import type { RangeMarker } from '../../layered-diffs/types';

export interface ActionMetadata {
  authorId: string;
  reasoning: string;
  /**
   * Source for this marker's edits. Used during subsumption-homogenization
   * to rewrite the `source` on any AI-source `suggestedEdit` that ends up
   * inside this marker's zone. Undefined for a user-attributed
   * materialization — markers are AI-suggestion chrome, so a sourceless
   * group gets no marker pair at all.
   */
  source: SuggestionSource | undefined;
}

export interface InsertSuggestionMarkersFromRangeMarkersOptions {
  /**
   * Map from groupId to action metadata (reasoning, etc.)
   */
  actionMetadataByGroupId: Map<string, ActionMetadata>;
  /**
   * The path to the paragraph element containing the text
   */
  paragraphPath: Path;
  /**
   * Range markers extracted from the diff result
   */
  rangeMarkers: RangeMarker[];
  /**
   * Optional logical-subsumption hint: for each new pair's `groupId`, the
   * set of existing `markerId`s that MUST be subsumed by it regardless of
   * position overlap. Used to merge a marker pair whose new delete
   * cancelled an `insertNode` that lived inside an existing marker pair
   * (a "cancellation chain"). Without this hint, position-based
   * subsumption would miss the case because the cancelled text collapses
   * the would-be overlap into a boundary-touching point.
   */
  mustSubsumeMarkerIdsByNewGroupId?: Map<string, ReadonlySet<number>>;
}

/**
 * Gets the total text length of a paragraph node's children.
 */
function getParagraphTextLength(node: Node): number {
  if (!('children' in node) || !Array.isArray(node.children)) return 0;

  let length = 0;
  for (const child of node.children) {
    if (Text.isText(child)) {
      length += child.text.length;
    } else if (Element.isElement(child) && Array.isArray(child.children)) {
      for (const innerChild of child.children) {
        if (Text.isText(innerChild)) {
          length += innerChild.text.length;
        }
      }
    }
  }
  return length;
}

/**
 * Converts a character offset within a paragraph (or across subsequent sibling
 * paragraphs) to a Slate Point.
 *
 * When an AI edit includes newlines, the original paragraph gets split into
 * multiple sibling paragraphs by `applyAlineChanges`. The offset from
 * `extractRangeMarkers` counts `\n` characters, so offsets beyond the first
 * paragraph's text need to be resolved in subsequent siblings.
 *
 * For each paragraph boundary crossed, we subtract the paragraph's text length
 * plus 1 (for the `\n` that became a paragraph break) from the remaining offset.
 */
function offsetToPoint(editor: Editor, paragraphPath: Path, targetOffset: number): Point | null {
  let currentPath = paragraphPath;
  let remainingOffset = targetOffset;

  // Walk across sibling paragraphs if the offset exceeds the current paragraph

  while (true) {
    const result = offsetToPointInParagraph(editor, currentPath, remainingOffset);
    if (result) return result;

    // Offset exceeds this paragraph — try the next sibling
    const [paragraphNode] = Editor.node(editor, currentPath);
    if (!paragraphNode) return null;

    const textLength = getParagraphTextLength(paragraphNode);

    // Subtract this paragraph's text length + 1 for the newline/paragraph break
    remainingOffset = remainingOffset - textLength - 1;
    if (remainingOffset < 0) return null;

    // Move to next sibling paragraph
    const nextPath = Path.next(currentPath);

    // Check if the next sibling exists
    if (!Editor.hasPath(editor, nextPath)) return null;
    currentPath = nextPath;
  }
}

/**
 * Converts a character offset to a Slate Point within a single paragraph.
 * Returns null if the offset exceeds the paragraph's text length.
 *
 * This walks through the paragraph's children, accumulating text length
 * until it reaches the target offset. The offset represents position in the
 * combined text of all children, including text inside inline elements
 * (textfields, links, etc.).
 *
 * Note: Markers can only be inserted into direct text children of the paragraph,
 * not inside inline elements. If the target offset falls within an inline element's
 * text, we return a point at the boundary of that element.
 */
function offsetToPointInParagraph(
  editor: Editor,
  paragraphPath: Path,
  targetOffset: number
): Point | null {
  const [paragraphNode] = Editor.node(editor, paragraphPath);

  if (!paragraphNode || !('children' in paragraphNode)) {
    return null;
  }

  let currentOffset = 0;

  for (let i = 0; i < paragraphNode.children.length; i++) {
    const child = paragraphNode.children[i];

    if (Text.isText(child)) {
      const textLength = child.text.length;

      // Check if target offset falls within this text node
      if (currentOffset + textLength >= targetOffset) {
        const offsetWithinText = targetOffset - currentOffset;
        return {
          offset: offsetWithinText,
          path: [...paragraphPath, i],
        };
      }

      currentOffset += textLength;
    } else if (Element.isElement(child) && Array.isArray(child.children)) {
      // Handle inline elements (textfield, link, etc.) that contain text children.
      // We need to count the text length inside them for offset calculation,
      // but we cannot insert markers inside inline elements.
      let inlineTextLength = 0;
      for (const innerChild of child.children) {
        if (Text.isText(innerChild)) {
          inlineTextLength += innerChild.text.length;
        }
      }

      if (currentOffset + inlineTextLength >= targetOffset) {
        // Target offset falls within this inline element's text.
        // We cannot insert inside it, so find the next text node after it.
        for (let j = i + 1; j < paragraphNode.children.length; j++) {
          const nextChild = paragraphNode.children[j];
          if (Text.isText(nextChild)) {
            return {
              offset: 0,
              path: [...paragraphPath, j],
            };
          }
        }
        return null;
      }

      currentOffset += inlineTextLength;
    }
  }

  // Offset exceeds this paragraph — return null so the caller can try the next sibling
  return null;
}

/**
 * Groups range markers by their id, pairing start and end markers.
 */
function groupRangeMarkersById(rangeMarkers: RangeMarker[]) {
  const groups = new Map<string, { end?: RangeMarker; start?: RangeMarker }>();

  for (const marker of rangeMarkers) {
    const existing = groups.get(marker.id) ?? {};
    if (marker.type === 'start') {
      existing.start = marker;
    } else {
      existing.end = marker;
    }
    groups.set(marker.id, existing);
  }

  return groups;
}

interface ExistingMarkerPair {
  endOffset: number;
  markerId: number;
  startOffset: number;
  suggestionId: string;
}

/**
 * Walks the paragraph (one level deep, including text inside inline
 * elements) and records every existing suggestion-marker pair in
 * paragraph-offset coordinates. The text inside marker elements does not
 * contribute to offsets — markers occupy a zero-width position in offset
 * space, matching the convention used by `extractRangeMarkers`.
 *
 * Only pairs whose start AND end are both present in this paragraph are
 * returned. (Sibling-paragraph markers — possible only in the rare
 * newline-split case — are not considered for subsumption today.)
 */
function gatherExistingMarkerPairs(editor: Editor, paragraphPath: Path): ExistingMarkerPair[] {
  const [paragraphNode] = Editor.node(editor, paragraphPath);
  if (!paragraphNode || !('children' in paragraphNode) || !Array.isArray(paragraphNode.children)) {
    return [];
  }

  const partials = new Map<
    number,
    { endOffset?: number; startOffset?: number; suggestionId?: string }
  >();
  let offset = 0;

  for (const child of paragraphNode.children) {
    if (Text.isText(child)) {
      offset += child.text.length;
      continue;
    }
    if (!Element.isElement(child)) continue;

    if (child.type === suggestionMarkerStartType) {
      const start = child as SuggestionMarkerStartElement;
      const partial = partials.get(start.markerId) ?? {};
      partial.suggestionId = start.suggestionId;
      partial.startOffset = offset;
      partials.set(start.markerId, partial);
      continue;
    }
    if (child.type === suggestionMarkerEndType) {
      const end = child as SuggestionMarkerEndElement;
      const partial = partials.get(end.markerId) ?? {};
      partial.endOffset = offset;
      partials.set(end.markerId, partial);
      continue;
    }

    // Inline element (textfield, link, etc.) — count its text children
    // toward the offset but don't recurse for markers (markers cannot live
    // inside inline elements).
    if (Array.isArray(child.children)) {
      for (const inner of child.children) {
        if (Text.isText(inner)) offset += inner.text.length;
      }
    }
  }

  const pairs: ExistingMarkerPair[] = [];
  for (const [markerId, partial] of partials) {
    if (
      partial.suggestionId !== undefined &&
      partial.startOffset !== undefined &&
      partial.endOffset !== undefined
    ) {
      pairs.push({
        endOffset: partial.endOffset,
        markerId,
        startOffset: partial.startOffset,
        suggestionId: partial.suggestionId,
      });
    }
  }
  return pairs;
}

interface PlannedNewPair {
  endOffset: number;
  groupId: string;
  markerId: number;
  reasoning: string;
  startOffset: number;
  authorId?: string;
  source?: SuggestionSource;
}

/**
 * Open-interval overlap test: two ranges intersect iff their interiors
 * share at least one point. Touching at a single offset (e.g. `e1 === s2`)
 * is NOT an intersection.
 */
function rangesOverlap(
  a: { endOffset: number; startOffset: number },
  b: { endOffset: number; startOffset: number }
): boolean {
  return Math.max(a.startOffset, b.startOffset) < Math.min(a.endOffset, b.endOffset);
}

/**
 * Computes the union closure for each new marker pair against existing
 * pairs and other new pairs:
 *
 * - A new pair that overlaps an existing pair grows to the union of the
 *   two ranges; the existing pair is marked for removal.
 * - Growth can chain: an expanded new pair may now overlap another
 *   existing or new pair, which also gets folded in. Iterate until no
 *   more expansion happens.
 * - Two new pairs that overlap each other merge into one (defensive —
 *   shouldn't happen given how range markers are emitted, but produces
 *   the right semantics if it ever does).
 */
function computeSubsumptionPlan(
  newPairs: PlannedNewPair[],
  existingPairs: ExistingMarkerPair[],
  mustSubsumeMarkerIdsByNewGroupId?: Map<string, ReadonlySet<number>>
): { finalNewPairs: PlannedNewPair[]; subsumedExistingMarkerIds: Set<number> } {
  const finalPairs = newPairs.map(p => ({ ...p }));
  const subsumed = new Set<number>();

  let changed = true;
  while (changed) {
    changed = false;

    for (let i = 0; i < finalPairs.length; i++) {
      const pair = finalPairs[i];
      if (!pair) continue;

      // Logical-subsumption pass: force-subsume any existing pair whose
      // markerId is in the must-subsume set for this new pair's groupId,
      // regardless of position overlap. Used for cancellation chains
      // where the new pair's delete cancelled an `insertNode` that lived
      // inside an existing marker — that overlap collapses to a single
      // boundary point in post-cancellation offset space and would be
      // missed by the position-based check below.
      const forcedIds = mustSubsumeMarkerIdsByNewGroupId?.get(pair.groupId);
      if (forcedIds) {
        for (const ex of existingPairs) {
          if (subsumed.has(ex.markerId)) continue;
          if (!forcedIds.has(ex.markerId)) continue;
          pair.startOffset = Math.min(pair.startOffset, ex.startOffset);
          pair.endOffset = Math.max(pair.endOffset, ex.endOffset);
          subsumed.add(ex.markerId);
          changed = true;
        }
      }

      for (const ex of existingPairs) {
        if (subsumed.has(ex.markerId)) continue;
        if (!rangesOverlap(pair, ex)) continue;
        pair.startOffset = Math.min(pair.startOffset, ex.startOffset);
        pair.endOffset = Math.max(pair.endOffset, ex.endOffset);
        subsumed.add(ex.markerId);
        changed = true;
      }

      for (let j = finalPairs.length - 1; j > i; j--) {
        const other = finalPairs[j];
        if (!other || !rangesOverlap(pair, other)) continue;
        pair.startOffset = Math.min(pair.startOffset, other.startOffset);
        pair.endOffset = Math.max(pair.endOffset, other.endOffset);
        finalPairs.splice(j, 1);
        changed = true;
      }
    }
  }

  return { finalNewPairs: finalPairs, subsumedExistingMarkerIds: subsumed };
}

/**
 * Removes any `suggestionMarkerStart` / `suggestionMarkerEnd` elements
 * whose `markerId` appears in `subsumedMarkerIds`. Walks the paragraph in
 * reverse so earlier-removed paths don't shift indices for later removals.
 */
function removeSubsumedMarkers(
  editor: Editor,
  paragraphPath: Path,
  subsumedMarkerIds: Set<number>
): void {
  if (subsumedMarkerIds.size === 0) return;

  const [paragraphNode] = Editor.node(editor, paragraphPath);
  if (!paragraphNode || !('children' in paragraphNode) || !Array.isArray(paragraphNode.children)) {
    return;
  }

  for (let i = paragraphNode.children.length - 1; i >= 0; i--) {
    const child = paragraphNode.children[i];
    if (!Element.isElement(child)) continue;
    if (child.type !== suggestionMarkerStartType && child.type !== suggestionMarkerEndType) {
      continue;
    }
    if (!('markerId' in child) || typeof child.markerId !== 'number') continue;
    if (!subsumedMarkerIds.has(child.markerId)) continue;
    Transforms.removeNodes(editor, { at: [...paragraphPath, i], voids: true });
  }
}

/**
 * Re-walks the paragraph to find a marker element by `markerId`, returning
 * its child index (or -1 if not found). Used after insertion to locate the
 * just-inserted start/end so the homogenization step can scan between them.
 */
function findMarkerChildIndex(
  editor: Editor,
  paragraphPath: Path,
  markerId: number,
  type: typeof suggestionMarkerStartType | typeof suggestionMarkerEndType
): number {
  const [paragraphNode] = Editor.node(editor, paragraphPath);
  if (!paragraphNode || !('children' in paragraphNode) || !Array.isArray(paragraphNode.children)) {
    return -1;
  }
  for (let i = 0; i < paragraphNode.children.length; i++) {
    const child = paragraphNode.children[i];
    if (!Element.isElement(child)) continue;
    if (child.type !== type) continue;
    if ('markerId' in child && child.markerId === markerId) return i;
  }
  return -1;
}

/**
 * Rewrites every AI-source `suggestedEdit` on `textNode` to use the new
 * authorId + source. User-manual edits (no `source`) are left untouched —
 * we never re-attribute a user's edit to the AI.
 */
function homogenizeTextNodeIfNeeded(
  editor: Editor,
  textPath: Path,
  textNode: AlineText,
  newAuthorId: string,
  newSource: SuggestionSource
): void {
  const edits = textNode.suggestedEdits;
  if (!edits?.length) return;

  let changed = false;
  const rewritten = edits.map(edit => {
    // Only InsertEdit and DeleteEdit carry an optional `source`. Anything
    // else (e.g. TextFormatEdit) doesn't, and is left as-is.
    if (edit.type !== 'insertNode' && edit.type !== 'deleteNode') return edit;
    const existingSource = edit.source;
    if (!existingSource) return edit; // User-manual edit — never rewrite.
    if (edit.authorId === newAuthorId && getIsSameSuggestionSource(existingSource, newSource)) {
      return edit;
    }
    changed = true;
    return { ...edit, authorId: newAuthorId, source: newSource };
  });

  if (changed) {
    Transforms.setNodes(editor, { suggestedEdits: rewritten }, { at: textPath, voids: true });
  }
}

/**
 * Walks text nodes between the start and end marker for the given
 * `markerId` (and one level into any inline-element wrappers in that
 * range) and homogenizes their AI-source edits to the new marker's
 * authorId + source.
 */
function homogenizeEditsForMarker(
  editor: Editor,
  paragraphPath: Path,
  markerId: number,
  newAuthorId: string,
  newSource: SuggestionSource
): void {
  const startIdx = findMarkerChildIndex(editor, paragraphPath, markerId, suggestionMarkerStartType);
  const endIdx = findMarkerChildIndex(editor, paragraphPath, markerId, suggestionMarkerEndType);
  if (startIdx === -1 || endIdx === -1 || startIdx >= endIdx) return;

  const [paragraphNode] = Editor.node(editor, paragraphPath);
  if (!paragraphNode || !('children' in paragraphNode) || !Array.isArray(paragraphNode.children)) {
    return;
  }

  for (let i = startIdx + 1; i < endIdx; i++) {
    const child = paragraphNode.children[i];
    if (Text.isText(child)) {
      homogenizeTextNodeIfNeeded(editor, [...paragraphPath, i], child, newAuthorId, newSource);
      continue;
    }
    if (Element.isElement(child) && Array.isArray(child.children)) {
      // Inline element — homogenize its text children one level deep.
      for (let j = 0; j < child.children.length; j++) {
        const inner = child.children[j];
        if (Text.isText(inner)) {
          homogenizeTextNodeIfNeeded(
            editor,
            [...paragraphPath, i, j],
            inner,
            newAuthorId,
            newSource
          );
        }
      }
    }
  }
}

/**
 * Inserts suggestion markers (`SuggestionMarkerStartElement` and
 * `SuggestionMarkerEndElement`) at positions indicated by range markers,
 * with two important behaviors when the new marker's range intersects
 * any existing marker pair already in the paragraph:
 *
 *   - **Subsumption.** If a new marker's range overlaps an existing
 *     marker pair's range (open-interval overlap — boundary-touching does
 *     NOT count), the existing pair's start/end elements are removed and
 *     the new marker's effective range expands to the union of the two.
 *     The new marker can only grow, never shrink. Chain growth and
 *     multiple-overlap cases are handled iteratively.
 *
 *   - **Homogenization.** After a new marker is placed, every
 *     `suggestedEdit` on a text node inside its zone whose `source` field
 *     is present (i.e. an AI edit) is rewritten so its `authorId` and
 *     `source` match the new marker's. User-manual edits (no `source`)
 *     are left untouched so the user's authorship survives. The goal is
 *     for accept/reject to act cleanly on the whole zone.
 *
 * Only sourced groups get markers: a group whose metadata carries no source
 * is a user-attributed materialization, and its edits must land with no
 * AI-suggestion chrome at all.
 *
 * The flow:
 *   1. Group range markers by id, validate start/end pairs.
 *   2. Walk the paragraph once to collect existing marker pairs in
 *      offset space.
 *   3. Compute the subsumption plan: final ranges for new pairs, and
 *      the set of existing pairs to remove.
 *   4. Remove subsumed existing markers (reverse path order).
 *   5. Insert new markers at their final offsets (reverse offset order
 *      so earlier insertions don't shift later ones).
 *   6. Homogenize AI-source edits inside each new marker's final zone.
 */
export function insertSuggestionMarkersFromRangeMarkers(
  editor: Editor,
  options: InsertSuggestionMarkersFromRangeMarkersOptions
): void {
  const { actionMetadataByGroupId, mustSubsumeMarkerIdsByNewGroupId, paragraphPath, rangeMarkers } =
    options;

  const groups = groupRangeMarkersById(rangeMarkers);

  // Step 1: gather existing marker pairs in offset space.
  const existingPairs = gatherExistingMarkerPairs(editor, paragraphPath);

  // Step 2: build planned new pairs with metadata + offsets. Only fully-
  // paired range markers participate in subsumption; half-pairs (which
  // shouldn't happen under the current upstream contract) are still
  // tracked for the single-side insertion path below.
  let nextMarkerId = Queries.getNextAvailableSuggestionMarkerId(editor);
  const fullNewPairs: PlannedNewPair[] = [];
  const halfPairs: Array<{
    groupId: string;
    markerId: number;
    offset: number;
    reasoning: string;
    type: 'start' | 'end';
    authorId?: string;
  }> = [];

  for (const [groupId, { end, start }] of groups) {
    const metadata = actionMetadataByGroupId.get(groupId);
    /**
     * No source, no marker: a sourceless group is a user-attributed
     * materialization, and marker pairs are AI-suggestion chrome (highlight
     * range, reasoning tooltip, the AI-suggestions toolbar). Existing marker
     * pairs the group's edits land inside stay untouched — the same as when
     * a user types inside a marked range.
     */
    if (!metadata?.source) continue;
    const markerId = nextMarkerId++;
    if (start && end) {
      fullNewPairs.push({
        authorId: metadata?.authorId,
        endOffset: end.offset,
        groupId,
        markerId,
        reasoning: metadata?.reasoning ?? '',
        source: metadata?.source,
        startOffset: start.offset,
      });
    } else if (start) {
      halfPairs.push({
        authorId: metadata?.authorId,
        groupId,
        markerId,
        offset: start.offset,
        reasoning: metadata?.reasoning ?? '',
        type: 'start',
      });
    } else if (end) {
      halfPairs.push({
        groupId,
        markerId,
        offset: end.offset,
        reasoning: metadata?.reasoning ?? '',
        type: 'end',
      });
    }
  }

  // Step 3: subsumption plan.
  const { finalNewPairs, subsumedExistingMarkerIds } = computeSubsumptionPlan(
    fullNewPairs,
    existingPairs,
    mustSubsumeMarkerIdsByNewGroupId
  );

  // Step 4: remove subsumed existing markers. Marker elements are
  // zero-width in offset space, so removing them doesn't shift the
  // offsets used by `offsetToPoint` in step 5.
  removeSubsumedMarkers(editor, paragraphPath, subsumedExistingMarkerIds);

  // Step 5: insert new markers at their final offsets, in reverse-offset
  // order so earlier-offset insertions aren't displaced. Half-pairs (if
  // any) are interleaved in the same sort.
  type Insertion =
    | {
        groupId: string;
        kind: 'start';
        markerId: number;
        offset: number;
        reasoning: string;
        authorId?: string;
      }
    | {
        kind: 'end';
        markerId: number;
        offset: number;
      };

  const insertions: Insertion[] = [];
  for (const pair of finalNewPairs) {
    insertions.push({
      authorId: pair.authorId,
      groupId: pair.groupId,
      kind: 'start',
      markerId: pair.markerId,
      offset: pair.startOffset,
      reasoning: pair.reasoning,
    });
    insertions.push({
      kind: 'end',
      markerId: pair.markerId,
      offset: pair.endOffset,
    });
  }
  for (const half of halfPairs) {
    if (half.type === 'start') {
      insertions.push({
        authorId: half.authorId,
        groupId: half.groupId,
        kind: 'start',
        markerId: half.markerId,
        offset: half.offset,
        reasoning: half.reasoning,
      });
    } else {
      insertions.push({ kind: 'end', markerId: half.markerId, offset: half.offset });
    }
  }
  insertions.sort((a, b) => b.offset - a.offset);

  for (const insertion of insertions) {
    const point = offsetToPoint(editor, paragraphPath, insertion.offset);
    if (!point) continue;

    if (insertion.kind === 'start') {
      const startElement: SuggestionMarkerStartElement = {
        ...(insertion.authorId !== undefined && { authorId: insertion.authorId }),
        children: [createDefaultText()],
        markerId: insertion.markerId,
        reasoning: insertion.reasoning,
        suggestedEdits: [],
        suggestionActionType: 'edit',
        suggestionId: 'groupId' in insertion ? insertion.groupId : '',
        type: suggestionMarkerStartType,
      };
      Transforms.insertNodes(editor, startElement, { at: point, voids: true });
    } else {
      const endElement: SuggestionMarkerEndElement = {
        children: [createDefaultText()],
        markerId: insertion.markerId,
        suggestedEdits: [],
        type: suggestionMarkerEndType,
      };
      Transforms.insertNodes(editor, endElement, { at: point, voids: true });
    }
  }

  // Step 6: homogenize AI-source edits inside each new marker's zone.
  // We re-walk the paragraph for each pair to find the inserted start/end
  // by `markerId`. Skipped when no source was provided in metadata
  // (back-compat for callers that don't yet thread source through).
  for (const pair of finalNewPairs) {
    if (!pair.source) continue;
    homogenizeEditsForMarker(
      editor,
      paragraphPath,
      pair.markerId,
      pair.authorId ?? '',
      pair.source
    );
  }
}
